import React, { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

import styles from "./PromoCarousel.module.scss";
import { PromoInterface } from "../../types/interfaces";
import { promoRoute } from "../../utils/constants";
import {
  getClusterImageUrl,
  getFirstImage,
} from "../../utils/functions/getImageUri";
import { selectClusterList } from "../../../store/slices/clusterListSlice";

interface Props {
  promos: PromoInterface[];
}

// Оборачиваем в memo, чтобы поиск не дергал карусель
export const PromoCarousel: React.FC<Props> = React.memo(({ promos }) => {
  const clusters = useSelector(selectClusterList);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    dragFree: true, // Позволяет свободно скроллить
  });

  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);

  const scrollPrev = useCallback(
    () => emblaApi && emblaApi.scrollPrev(),
    [emblaApi],
  );
  const scrollNext = useCallback(
    () => emblaApi && emblaApi.scrollNext(),
    [emblaApi],
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setPrevBtnEnabled(emblaApi.canScrollPrev());
    setNextBtnEnabled(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  if (!promos || promos.length === 0) return null;

  return (
    <div className={styles.Wrapper}>
      <div className={styles.Viewport} ref={emblaRef}>
        <div className={styles.Container}>
          {promos.map((promo) => {
            const cluster = clusters.find((c) => c.id === promo.clusterId);
            const image = getFirstImage(promo.files);
            const hasDiscount = promo.discount > 0;

            return (
              <Link
                key={promo.id}
                to={promoRoute(promo.id)}
                className={styles.Slide}
              >
                <div className={styles.ImageWrap}>
                  {image && cluster ? (
                    <img
                      src={getClusterImageUrl(cluster, image)}
                      alt={promo.name}
                      className={styles.Image}
                      loading="lazy"
                    />
                  ) : (
                    <div className={styles.ImagePlaceholder} />
                  )}
                  {hasDiscount && (
                    <div className={styles.Badge}>-{promo.discount}%</div>
                  )}
                </div>
                <div className={styles.Info}>
                  <h3 className={styles.Name}>{promo.name}</h3>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Кнопки навигации (опционально, можно скрыть на мобилках) */}
      <button
        className={styles.PrevBtn}
        onClick={scrollPrev}
        disabled={!prevBtnEnabled}
      >
        ‹
      </button>
      <button
        className={styles.NextBtn}
        onClick={scrollNext}
        disabled={!nextBtnEnabled}
      >
        ›
      </button>
    </div>
  );
});
